import { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import { ResolverTag } from '@ps/web-core-enums';
import { canUseDOM } from '@ps/web-core-utils';
import type { PokerPowerPath } from '@ps/web-martech-cms-types';

import type { WidgetEffectParams } from '@swf/core/store';
import { fetchJson } from '@swf/core/utils/api';

import { ENDPOINTS } from '../constants';
import type { DataResponse, PassBundleRaw } from '../types';
import { getPPDataTypeKey } from '../utils';

import type { WidgetDispatch } from './types';

export const effects = {
  async preload(_payload: undefined, { dispatch, widget }: WidgetEffectParams<PokerPowerPath>) {
    const { clear, dataRequested, passesDataRequested, subscribeToSteps }: WidgetDispatch =
      dispatch[widget.uid];

    await dataRequested();

    if (canUseDOM) {
      subscribeToSteps();
      passesDataRequested();
    }

    widget.unmount.subscribe(() => clear());
  },
  async dataRequested(
    _payload: undefined,
    { dispatch, select, widget, request, rootState }: WidgetEffectParams<PokerPowerPath>
  ): Promise<void> {
    const { dataLoadFailure, updateSteps }: WidgetDispatch = dispatch[widget.uid];
    const { mttDataType = '' } = select.config.get(rootState);
    const DATA_KEY = getPPDataTypeKey(mttDataType);

    try {
      const { body } = await request({
        service: ENDPOINTS.HTTP[DATA_KEY],
        http: true
      });

      updateSteps(body);
    } catch {
      dataLoadFailure();
    }
  },
  async subscribeToSteps(
    _payload: undefined,
    { dispatch, select, widget, rootState, requestStream }: WidgetEffectParams<PokerPowerPath>
  ): Promise<void> {
    const { updateSteps }: WidgetDispatch = dispatch[widget.uid];
    const options = select.cms.getRequestOptions(rootState);
    const { mttDataType = '' } = select.config.get(rootState);
    const DATA_KEY = getPPDataTypeKey(mttDataType);

    requestStream<DataResponse>({
      service: ENDPOINTS.WS[DATA_KEY],
      ...options
    }).subscribe(({ data }) => updateSteps(data));
  },
  async passesDataRequested(
    _payload: undefined,
    { dispatch, select, widget, rootState, request }: WidgetEffectParams<PokerPowerPath>
  ) {
    const { setPassesData, setPassesError }: WidgetDispatch = dispatch[widget.uid];
    const siteId = select.cms.getSiteId(rootState);

    try {
      const { body } = await request({
        service: 'tags.resolve',
        body: { tag: ResolverTag.PowerPathPassesData, brand: BrandTypes.POKER_STARS, site: siteId }
      });
      const passesBundleResponse: PassBundleRaw = await fetchJson(body?.url);

      setPassesData(passesBundleResponse);
    } catch (err) {
      const errMessage = (err as Error)?.message;
      setPassesError(errMessage);
    }
  }
};
